/* eslint-disable max-lines-per-function */
/** @typedef {new (...args: any[]) => {}} Constructor */

/**
 * @template {!Constructor} T
 * @param {T} superClass - The class to extend
 */
export const FormControlMixin = superClass => class FormControl extends superClass {
	static formAssociated = true;

	static get properties () {
		return {
			disabled: {
				attribute: 'disabled',
				reflect: true,
				type: Boolean
			},
			name: {
				attribute: 'name',
				reflect: true,
				type: String
			},
			required: {
				attribute: 'required',
				reflect: true,
				type: Boolean
			},
			value: {
				attribute: 'value',
				reflect: true,
				type: String
			}
		};
	}

	get form () {
		return this._internals.form;
	}

	/**
	 * @type {ValidityStateFlags}
	 */
	get validity () {
		return this.inputRef?.value?.validity ?? {};
	}

	/**
	 * @type {string}
	 */
	get validationMessage () {
		return this.inputRef?.value?.validationMessage;
	}

	/**
	 * Array of callback function used to provide a custom error validation message.
	 * @type {((element: HTMLInputElement) => string)[]}
	 */
	get customErrorValidators () {
		return [];
	}

	/**
	 * @type {import('lit/directives/ref.js').Ref<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}
	 */
	inputRef;

	/**
	 * @param  {...any} args
	 */
	constructor (...args) {
		super(...args);

		/**
		 * @type {ElementInternals}
		 */
		this._internals = this.attachInternals();

		/**
		 * @type {boolean}
		 */
		this.disabled = false;

		/**
		 * @type {string}
		 */
		this.name = '';

		/**
		 * @type {boolean}
		 */
		this.required = false;

		/**
		 * @type {string}
		 */
		this.value = '';
	}

	checkValidity () {
		return this._internals.checkValidity();
	}

	reportValidity () {
		return this._internals.reportValidity();
	}

	/**
	 * Set form value and validity states via the ElementInternals API.
	 */
	updateElementInternals () {
		// Form value update
		this._internals.setFormValue(this.value);

		// Custom Error Validation
		const customErrorMessage = this.customErrorValidators
			?.map(customErrorValidator => customErrorValidator(this.inputRef?.value))
			?.find(invalid => invalid) || '';

		// Set custom message and report immediately if message is found
		this.inputRef?.value?.setCustomValidity(customErrorMessage);
		if (customErrorMessage) {
			this.inputRef?.value?.reportValidity();
		}

		// Set validity via internals
		this._internals.setValidity(this.validity, this.validationMessage, this.inputRef?.value);
	}

	firstUpdated () {
		super.firstUpdated?.();

		this.updateElementInternals();
	}

	async updated (changedProperties) {
		await this.updateComplete;

		this.updateElementInternals();

		if (changedProperties.has('value') && this.value?.length > 0 && changedProperties.get('value') !== this.value) {
			this.dispatchEvent(new CustomEvent('change', {
				bubbles: true,
				composed: true
			}));
		}
	}

	/**
	 * Event handler for the input's change event.
	 */
	handleInputChange (e) {
		this.value = e.target.value;

		this.updateElementInternals();

		this.dispatchEvent(new CustomEvent('change', {
			bubbles: true,
			composed: true
		}));
	}
};
